import { useEffect, useState } from 'react';

// Ticks down to a wall-clock instant; returns remaining whole seconds.
//   unlockAt === null      → null (no active lock)
//   unlockAt in the past   → 0 immediately
//   unlockAt in the future → seconds remaining, auto-stops at 0
export function useCountdown(unlockAt: number | null): number | null {
  const [remaining, setRemaining] = useState<number | null>(() =>
    computeRemaining(unlockAt),
  );

  useEffect(() => {
    // Reset in the effect body (not only cleanup) so StrictMode's double-invoke
    // doesn't strand the component with a stale null from the prior teardown.
    if (unlockAt === null) {
      setRemaining(null);
      return;
    }
    setRemaining(computeRemaining(unlockAt));
    if (Date.now() >= unlockAt) return;

    const id = window.setInterval(() => {
      const r = computeRemaining(unlockAt);
      setRemaining(r);
      if (r !== null && r <= 0) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [unlockAt]);

  return remaining;
}

function computeRemaining(unlockAt: number | null): number | null {
  if (unlockAt === null) return null;
  return Math.max(0, Math.ceil((unlockAt - Date.now()) / 1000));
}
